import React from "react";
import { KeyTerm, ImportantNote } from "../note-components";
import type { UnitNotes } from "../types";

const governmentUnit3Notes: UnitNotes = {
  unitId: "unit-3",
  unitName: "Unit 3: Political Culture and Participation",
  examWeight: "15–20%",
  focus: "The relationship between the state and society, including how citizens define interests, organize themselves, and participate in politics.",
  sections: [
    {
      id: "topic-3-1",
      title: "Topic 3.1: Civil Society",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🤝 Defining Civil Society</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-bold text-blue-800">Definition</h4>
              <p className="text-sm text-slate-700 mt-2">The aggregate of non-governmental organizations (NGOs) and institutions that manifest interests and will of citizens. Includes voluntary associations autonomous from the state.</p>
              <div className="mt-3 space-y-2">
                <p className="text-sm text-slate-700"><strong>Examples:</strong> Religious organizations, news media, professional associations, labor unions, charities, sports clubs.</p>
                <p className="text-sm text-slate-700"><strong>Role in Democracy:</strong> Checks government power, articulates interests, builds social capital (trust among citizens).</p>
                <p className="text-sm text-slate-700"><strong>Role in Authoritarianism:</strong> Often viewed as threat; regimes use co-optation or repression to control it.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Country Comparisons</h3>
            <div className="space-y-3">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🇬🇧 United Kingdom (Democratic)</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Status:</strong> Highly developed, pluralist, protected by law.</p>
                <p className="text-sm text-slate-700"><strong>Characteristics:</strong> Interest groups compete freely to influence policy (Pluralism).</p>
                <p className="text-sm text-slate-700"><strong>QUANGOs:</strong> Quasi-Autonomous Non-Governmental Organizations - state-funded but operate independently.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🇷🇺 Russia (Illiberal/Authoritarian)</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Status:</strong> Severely restricted under Putin.</p>
                <p className="text-sm text-slate-700"><strong>2012 Foreign Agent Law:</strong> NGOs receiving foreign funding must register as "foreign agents," subjecting them to audits and stigma.</p>
                <p className="text-sm text-slate-700"><strong>State Corporatism:</strong> Only state-sanctioned organizations allowed (e.g., Nashi youth group).</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🇨🇳 China (Authoritarian)</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Status:</strong> Heavily controlled; no autonomy.</p>
                <p className="text-sm text-slate-700"><strong>GONGOs:</strong> Government-Organized NGOs - appear independent but run by Communist Party.</p>
                <p className="text-sm text-slate-700"><strong>Restrictions:</strong> Crackdowns on religious groups (Falun Gong, underground churches) and non-state labor unions.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🇲🇽 Mexico (Transitioning/Democratic)</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Status:</strong> Growing rapidly since end of PRI one-party rule (2000).</p>
                <p className="text-sm text-slate-700"><strong>Evolution:</strong> PRI used State Corporatism to control unions. Today, genuine independent groups flourish, though cartel violence threatens activists.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">🇳🇬 Nigeria (Democratic/Fragile)</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Status:</strong> Active and vibrant but fragmented.</p>
                <p className="text-sm text-slate-700"><strong>Characteristics:</strong> Civil society played major role in ending military rule (1999). Labor unions and religious groups are powerful but divided along ethnic lines.</p>
                <p className="text-sm text-slate-700"><strong>Challenge:</strong> Boko Haram destroys civil society in North; government restricts social media.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🇮🇷 Iran (Theocratic/Authoritarian)</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Status:</strong> Weak and suppressed, but historically active during crises.</p>
                <p className="text-sm text-slate-700"><strong>Restrictions:</strong> Khatami presidency saw bloom in civil society, but hardliners cracked down. Professional syndicates and women's rights groups face harassment.</p>
                <p className="text-sm text-slate-700"><strong>Basij:</strong> Paramilitary militia that acts as "counter" civil society to enforce regime morality.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-2",
      title: "Topic 3.2: Political Culture",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🎭 Defining Political Culture</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-bold text-blue-800">Definition</h4>
              <p className="text-sm text-slate-700 mt-2">The collective attitudes, values, and beliefs of the citizenry and norms of behavior in the political system.</p>
              <div className="mt-3 space-y-2">
                <p className="text-sm text-slate-700"><strong>Socialization:</strong> Process by which political culture is transmitted (family, school, media, religious institutions).</p>
                <p className="text-sm text-slate-700"><strong>Consensual:</strong> Citizens generally agree on how decisions are made (UK).</p>
                <p className="text-sm text-slate-700"><strong>Conflictual:</strong> Citizens sharply divided on legitimacy of regime (Nigeria).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Country Comparisons</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🇬🇧 United Kingdom</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Gradualism:</strong> Belief in slow, evolutionary change rather than revolution.</p>
                <p className="text-sm text-slate-700"><strong>Deference to Authority:</strong> Traditionally high respect for rule of law.</p>
                <p className="text-sm text-slate-700"><strong>Insularity:</strong> Feeling of separation from Europe (driver of Brexit).</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🇷🇺 Russia</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Statism:</strong> Belief that state should take active role in citizens' lives; preference for strong leader over democratic checks.</p>
                <p className="text-sm text-slate-700"><strong>Mistrust:</strong> Deep skepticism of government (due to Soviet history) but high nationalism.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🇨🇳 China</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Confucianism:</strong> Respect for hierarchy, authority, and social harmony.</p>
                <p className="text-sm text-slate-700"><strong>Maoism:</strong> Egalitarianism and mass line (leaders consulting masses).</p>
                <p className="text-sm text-slate-700"><strong>Nationalism:</strong> Strong pride in Chinese history and recovery from "Century of Humiliation."</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🇲🇽 Mexico</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Patron-Clientelism (Camarillas):</strong> Exchange of political support for economic favors.</p>
                <p className="text-sm text-slate-700"><strong>Efficacy:</strong> Historically low trust in government due to rigged elections, but rising participation.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">🇳🇬 Nigeria</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Prebendalism:</strong> Belief that government officials have right to use state resources to benefit their ethnic or religious group.</p>
                <p className="text-sm text-slate-700"><strong>Low Legitimacy:</strong> Massive distrust due to corruption and failure to provide services.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🇮🇷 Iran</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Shi'a Islam:</strong> Centrality of religion in public life.</p>
                <p className="text-sm text-slate-700"><strong>Revolutionary Spirit:</strong> Tension between republicanism (voting) and theocracy (God's rule).</p>
                <p className="text-sm text-slate-700"><strong>Anti-Western:</strong> Historical grievance against US/UK interference.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-3",
      title: "Topic 3.3: Political Ideologies",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💡 Key Ideologies</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Liberalism (Individualism)" 
                definition="Emphasis on individual freedom, civil rights, and free markets. Dominant in UK."
              />
              <KeyTerm 
                term="Communism" 
                definition="Belief in abolition of private property and state control of economy to ensure equality. China."
              />
              <KeyTerm 
                term="Socialism" 
                definition="State ownership of key industries to reduce inequality, often coexisting with markets."
              />
              <KeyTerm 
                term="Fascism" 
                definition="Extreme nationalism, state over individual, authoritarianism."
              />
              <KeyTerm 
                term="Neoliberalism" 
                definition="Economic ideology favoring free markets, privatization, and reduced government spending (Austerity)."
              />
              <KeyTerm 
                term="Populism" 
                definition="Us (the people) vs. Them (the elite). Can be left or right-wing."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Application in Course Countries</h3>
            <div className="space-y-2">
              <p className="text-sm text-slate-700"><strong>🇨🇳 China:</strong> "Socialism with Chinese Characteristics" - political communism mixed with economic capitalism.</p>
              <p className="text-sm text-slate-700"><strong>🇬🇧 UK:</strong> Shifted from "Collectivist Consensus" (Welfare State) to "Thatcherism" (Neoliberalism) to "Third Way" (Centrism).</p>
              <p className="text-sm text-slate-700"><strong>🇮🇷 Iran:</strong> Khomeinism/Islamism - Governance based on Velayat-e Faqih (Guardianship of Islamic Jurist).</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-4",
      title: "Topic 3.4: Political Values and Beliefs",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ Rule of Law vs. Rule by Law</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Rule of Law (Democratic)</h4>
                <p className="text-sm text-slate-700 mt-2">Law applies equally to everyone, including government. Due process is followed.</p>
                <p className="text-xs text-green-600 mt-2">Examples: UK, Mexico, Nigeria (in principle)</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Rule BY Law (Authoritarian)</h4>
                <p className="text-sm text-slate-700 mt-2">Law is a tool used by state to control citizens. State is often above the law.</p>
                <p className="text-xs text-red-600 mt-2">Examples: China, Russia</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📈 Post-Materialism</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-bold text-blue-800">Definition</h4>
              <p className="text-sm text-slate-700 mt-2">A shift in values in advanced democracies where citizens prioritize quality of life (environment, gender equality, expression) over basic economic security.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Relevance:</strong> Most visible in UK and urban areas of Mexico. Less relevant in Nigeria, where basic needs are unmet.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🛡️ The Welfare State</h3>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h4 className="font-bold text-purple-800">Belief</h4>
              <p className="text-sm text-slate-700 mt-2">Government has responsibility to provide safety net (health care, pensions).</p>
              <div className="mt-3 space-y-2">
                <p className="text-sm text-slate-700"><strong>🇬🇧 UK:</strong> NHS (National Health Service) is "sacred" element of political culture.</p>
                <p className="text-sm text-slate-700"><strong>🇨🇳 China:</strong> "Iron Rice Bowl" (cradle-to-grave welfare) broken by capitalism, causing social unrest.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-5",
      title: "Topic 3.5: Nature and Role of Political Participation",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">✅ Democratic Participation</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200 mb-4">
              <h4 className="font-bold text-green-800">Goal</h4>
              <p className="text-sm text-slate-700 mt-2">To influence policy and hold leaders accountable.</p>
              <div className="mt-3 space-y-2">
                <p className="text-sm text-slate-700"><strong>Forms:</strong> Voting, contacting representatives, joining parties, protesting.</p>
                <p className="text-sm text-slate-700"><strong>Referenda:</strong> Allowing citizens to vote directly on policy. UK uses this (Brexit, Scottish Independence) to resolve constitutional issues.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">❌ Authoritarian Participation</h3>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200 mb-4">
              <h4 className="font-bold text-red-800">Goal</h4>
              <p className="text-sm text-slate-700 mt-2">To provide veneer of legitimacy, act as "safety valve" for anger, or intimidate opponents.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Coerced Participation:</strong> People forced or pressured to attend rallies or vote to show loyalty (common in China and Russia).</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Country Specifics</h3>
            <div className="space-y-2">
              <p className="text-sm text-slate-700"><strong>🇳🇬 Nigeria:</strong> Participation varies by region; violence often suppresses turnout.</p>
              <p className="text-sm text-slate-700"><strong>🇨🇳 China:</strong> Village Elections - competitive elections at local level to vent frustration, but not at national level.</p>
              <p className="text-sm text-slate-700"><strong>🇷🇺 Russia:</strong> High voter turnout, but allegations of "bus-ing" voters and ballot stuffing prove Putin's popularity.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-6",
      title: "Topic 3.6: Forces that Impact Political Participation",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🎯 Regime Type Impact</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Democratic</h4>
                <p className="text-sm text-slate-700 mt-2">Encourages participation but may restrict access through registration laws or gerrymandering.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Authoritarian</h4>
                <p className="text-sm text-slate-700 mt-2">Encourages supportive participation; suppresses oppositional participation.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🚧 Structural Barriers</h3>
            <div className="space-y-3">
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🇮🇷 Iran</h4>
                <p className="text-sm text-slate-700 mt-2">Guardian Council vets all candidates. In 2020/2021, disqualified almost all reformists, leading to historic low voter turnout (boycotts).</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">🇳🇬 Nigeria</h4>
                <p className="text-sm text-slate-700 mt-2">Poor infrastructure, threat of violence, lack of trust in INEC (Independent National Electoral Commission).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💰 Economic Forces</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-slate-700">Economic downturns often spur protest movements.</p>
              <p className="text-sm text-slate-700 mt-2">Examples: Iran's 2017-2019 protests over fuel prices; Russia's protests over pension age increases.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-7",
      title: "Topic 3.7: Civil Rights and Civil Liberties",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📋 Definition</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Civil Liberties</h4>
                <p className="text-sm text-slate-700 mt-2">Protection FROM the government (free speech, press, assembly).</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Civil Rights</h4>
                <p className="text-sm text-slate-700 mt-2">Protection AGAINST discrimination (equality for women, minorities).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Country Analysis</h3>
            <div className="space-y-3">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🇬🇧 United Kingdom</h4>
                <p className="text-sm text-slate-700 mt-2">Strong protections via Common Law and Human Rights Act. High transparency.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🇲🇽 Mexico</h4>
                <p className="text-sm text-slate-700 mt-2">Constitution guarantees rights, but weak judiciary and cartel violence lead to impunity. One of most dangerous countries for journalists.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🇳🇬 Nigeria</h4>
                <p className="text-sm text-slate-700 mt-2">Rights exist on paper but violated by police (SARS) and military. LGBTQ+ rights virtually non-existent (Same-Sex Marriage Prohibition Act).</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🇷🇺 Russia</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Media:</strong> State control of TV (propaganda). Independent journalists harassed or killed.</p>
                <p className="text-sm text-slate-700"><strong>Assembly:</strong> Protests require permits which are rarely granted.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🇨🇳 China</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Great Firewall:</strong> Massive internet censorship/surveillance.</p>
                <p className="text-sm text-slate-700"><strong>Social Credit System:</strong> Monitoring citizen behavior to reward/punish loyalty.</p>
                <p className="text-sm text-slate-700"><strong>Xinjiang:</strong> Mass internment of Uighur Muslims.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🇮🇷 Iran</h4>
                <p className="text-sm text-slate-700 mt-2">Severe restrictions on women (hijab laws), press censorship, religious minorities (Baha'is persecuted).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-8",
      title: "Topic 3.8: Political and Social Cleavages",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔀 Defining Cleavages</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-bold text-blue-800">Cleavage</h4>
              <p className="text-sm text-slate-700 mt-2">An internal division that structures society and creates distinct political interests.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Coinciding (Reinforcing)</h4>
                <p className="text-sm text-slate-700 mt-2">Every dispute aligns same groups against each other. DESTABILIZES society.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Cross-Cutting</h4>
                <p className="text-sm text-slate-700 mt-2">Divisions are mixed. STABILIZES society.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Country Cleavages</h3>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🇳🇬 Nigeria (Coinciding - Explosive)</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Region/Religion/Ethnicity:</strong></p>
                <p className="text-xs text-slate-700 mt-1">North: Muslim, Hausa-Fulani, Poorer</p>
                <p className="text-xs text-slate-700">South: Christian, Yoruba (SW)/Igbo (SE), Oil-rich (richer)</p>
                <p className="text-sm text-slate-700 mt-2"><strong>Result:</strong> Constant threat of secession and violence.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🇬🇧 United Kingdom (Cross-Cutting - Manageable)</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>National:</strong> Scottish, Welsh, Irish, English identities (Devolution is solution)</p>
                <p className="text-sm text-slate-700"><strong>Religious:</strong> Protestants vs. Catholics (Northern Ireland - The Troubles)</p>
                <p className="text-sm text-slate-700"><strong>Class:</strong> Working Class (Labour) vs. Middle/Upper Class (Conservative)</p>
                <p className="text-sm text-slate-700"><strong>Race:</strong> Tensions regarding immigrants from former colonies</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🇨🇳 China (Coinciding)</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Ethnic:</strong> Han (92%) vs. Uighurs (Muslim, Xinjiang) and Tibetans (Buddhist)</p>
                <p className="text-sm text-slate-700"><strong>Urban/Rural:</strong> Massive income inequality between coastal cities and rural interior</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🇲🇽 Mexico (Coinciding)</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Region:</strong> North (Industrial, Wealthier, Pro-Trade) vs. South (Agricultural, Poorer, Indigenous)</p>
                <p className="text-sm text-slate-700"><strong>Race/Class:</strong> Mestizo/White (Wealthier) vs. Amerindian (Poorer)</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">🇮🇷 Iran (Cross-Cutting & Coinciding)</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Religion:</strong> Shi'a (90%) vs. Sunni (10%) and Baha'i</p>
                <p className="text-sm text-slate-700"><strong>Ethnicity:</strong> Persian (Majority) vs. Azeri, Kurd, Arab</p>
                <p className="text-sm text-slate-700"><strong>Ideology:</strong> Reformist vs. Hardliner/Conservative</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🇷🇺 Russia (Coinciding)</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Ethnic/Religious:</strong> Ethnic Russians (Orthodox) vs. Chechens (Muslim/Separatist)</p>
                <p className="text-sm text-slate-700"><strong>Class:</strong> Oligarchs vs. Working Poor</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-9",
      title: "Topic 3.9: Challenges from Political and Social Cleavages",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔧 State Responses to Cleavages</h3>
            <p className="text-sm text-slate-700 mb-4">States must manage divisions to maintain legitimacy and stability.</p>
            <div className="space-y-3">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Devolution/Federalism</h4>
                <p className="text-sm text-slate-700 mt-2">Giving power to regions to satisfy local identity.</p>
                <p className="text-xs text-green-600 mt-2">Success: UK created Scottish Parliament</p>
                <p className="text-xs text-green-600">Attempt: Nigeria uses Federal character principle (quotas)</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Coercion/Repression</h4>
                <p className="text-sm text-slate-700 mt-2">Using force to silence separatist movements.</p>
                <p className="text-xs text-red-600 mt-2">China: "Re-education camps" in Xinjiang</p>
                <p className="text-xs text-red-600">Russia: Two wars in Chechnya to stop secession</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Co-optation</h4>
                <p className="text-sm text-slate-700 mt-2">Buying off dissenters.</p>
                <p className="text-xs text-blue-600 mt-2">Mexico: PRI patronage system historically incorporated peasant and labor groups</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚠️ Consequences</h3>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Separatism</h4>
                <p className="text-sm text-slate-700 mt-2">Groups demanding independence (Scotland, Chechnya, Biafra in Nigeria, Tibet)</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">Terrorism</h4>
                <p className="text-sm text-slate-700 mt-2">Boko Haram (Nigeria) fighting Western influence; unrest in Niger Delta over oil money</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Loss of Legitimacy</h4>
                <p className="text-sm text-slate-700 mt-2">If state favors one group (Persians in Iran, Han in China), minority groups withdraw support or rebel.</p>
              </div>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default governmentUnit3Notes;